import { useMemo } from "react";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { STAGE_COLORS, ehCorEscolhidaAmao } from "./production-stage-colors";
import { SeletorDeCorLivre } from "./SeletorDeCorLivre";

type Props = {
  valor: string | null;
  onChange: (hex: string) => void;
  disabled?: boolean;
  /** Rótulo de exemplo dentro da prévia — mostra a cor como ela vai aparecer de verdade. */
  previa?: string;
};

/**
 * Cor do estágio: paleta curada + qualquer cor (seletor de arrastar, em `SeletorDeCorLivre`).
 *
 * A paleta some da frente de quem só quer resolver rápido, e continua sendo a resposta certa na
 * maioria dos casos — seis cores distintas entre si e legíveis. O seletor livre existe pra quem tem
 * um fluxo com nome e cor próprios ("verde da Zircônia") e não deveria ter que escolher a cor mais
 * parecida.
 *
 * A cor vira FUNDO e TEXTO do mesmo rótulo (`${cor}1a` atrás, `cor` na frente). Escolha clara demais
 * produz etiqueta ilegível — então o que sai daqui já passou por `ajustarParaLeitura`, e a prévia
 * mostra o resultado real. Ajustar na escolha (um lugar) em vez de em cada tela que desenha a
 * etiqueta (oito) é o que mantém o resto do sistema sem saber que isso existe.
 *
 * Cor escolhida à mão não se perde: as que já estão em uso pelos estágios do laboratório voltam como
 * círculo ao lado da paleta. A memória são os próprios estágios, não um registro à parte — assim ela
 * vale pra equipe inteira e pra qualquer navegador, e some sozinha quando o último estágio daquela
 * cor deixa de existir.
 */
export function SeletorDeCorDoEstagio({ valor, onChange, disabled, previa = "Exemplo" }: Props) {
  const atual = valor ?? STAGE_COLORS[0];
  const stagesQuery = trpc.prosthesis.stages.list.useQuery();

  // A cor sendo editada entra mesmo sem estar salva ainda: acabou de sair do seletor livre e
  // precisa aparecer selecionada em algum lugar.
  const personalizadas = useMemo(() => {
    const cores = (stagesQuery.data ?? []).map((s: { colorHex: string | null }) => s.colorHex);
    if (valor) cores.push(valor);
    return [...new Set(cores.filter(ehCorEscolhidaAmao).map((c) => c!.toLowerCase()))];
  }, [stagesQuery.data, valor]);

  const selecionada = valor?.toLowerCase() ?? null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {[...STAGE_COLORS, ...personalizadas].map((c) => (
          <button
            key={c}
            type="button"
            disabled={disabled}
            onClick={() => onChange(c)}
            aria-label={`Cor ${c}`}
            aria-pressed={selecionada === c}
            className={cn(
              "h-6 w-6 rounded-full transition-transform disabled:opacity-50",
              selecionada === c ? "ring-2 ring-zinc-400 ring-offset-2 dark:ring-offset-card" : "hover:scale-110",
            )}
            style={{ backgroundColor: c }}
          />
        ))}

        {/* Pipeta = "uma cor que ainda não existe aqui": abre o seletor de arrastar, que só grava ao
            confirmar (a cor escolhida à mão ganha círculo próprio acima). */}
        <SeletorDeCorLivre valor={atual} disabled={disabled} previa={previa} onEscolher={onChange} />
      </div>

      <span
        className="inline-flex w-fit items-center rounded px-2 py-0.5 text-[11px] font-semibold"
        style={{ backgroundColor: `${atual}1a`, color: atual }}
      >
        {previa}
      </span>
    </div>
  );
}
